import { io } from 'socket.io-client';
import websocketConfig from '../config/websocket';
import { clearAuthData } from '../utils/auth';

class WebSocketService {
  constructor() {
    this.socket = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = websocketConfig.connection.maxReconnectAttempts;
    this.reconnectInterval = websocketConfig.connection.reconnectInterval;
    this.isConnecting = false;
    this.messageHandlers = new Map();
    this.isEnabled = websocketConfig.enabled;
    this.connectionFailures = 0;
    this.maxConnectionFailures = websocketConfig.connection.maxConnectionFailures;
    this.heartbeatTimer = null;
  }

  // 启用WebSocket功能
  enable() {
    this.isEnabled = true;
  }

  // 禁用WebSocket功能  
  disable() {
    this.isEnabled = false;
    this.disconnect();
  }

  connect(userAccountType) {
    // 检查是否启用WebSocket功能
    if (!this.isEnabled) {
      return;
    }

    // 所有账号类型都可以使用WebSocket连接（用于单点登录和会话管理）
    if (!userAccountType) {
      return;
    }

    // 检查是否已达到最大失败次数
    if (this.connectionFailures >= this.maxConnectionFailures) {
      this.isEnabled = false;
      return;
    }

    if (this.socket && this.socket.connected) {
      return;
    }

    if (this.isConnecting) {
      return;
    }

    this.isConnecting = true;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        this.isConnecting = false;
        return;
      }

      // 构建Socket.IO连接URL和选项
      let serverUrl;
      
      if (websocketConfig.server.url) {
        serverUrl = websocketConfig.server.url;
      } else {
        const protocol = window.location.protocol;
        const host = window.location.hostname;
        const port = websocketConfig.server.port || window.location.port;
        serverUrl = `${protocol}//${host}${port ? ':' + port : ''}`;
      }

      const socketOptions = {
        path: websocketConfig.server.path, // '/ws'
        query: {
          token: token, // 通过query参数传递token
          accountType: userAccountType // 传递账号类型
        },
        transports: ['websocket', 'polling'],
        forceNew: true,
        reconnection: false, // 我们自己处理重连
        timeout: 10000
      };

      // 创建Socket.IO连接
      this.socket = io(serverUrl, socketOptions);

      // 连接成功事件
      this.socket.on('connect', () => {
        this.reconnectAttempts = 0;
        this.connectionFailures = 0;
        this.isConnecting = false;
        this.startHeartbeat();
      });

      // 认证成功事件
      this.socket.on('authenticated', (data) => {
        // 认证成功，无需特殊处理
      });

      // 认证失败事件
      this.socket.on('auth_error', (data) => {
        this.connectionFailures++;
        this.disconnect();
      });

      // 接收消息事件（权限更新）
      this.socket.on('message', (data) => {
        this.handleMessage(data);
      });

      // 广播消息事件
      this.socket.on('broadcast', (data) => {
        this.handleMessage(data);
      });

      // 心跳事件
      this.socket.on('heartbeat', (data) => {
        // 心跳响应，无需特殊处理
      });

      // 连接错误事件
      this.socket.on('connect_error', (error) => {
        this.isConnecting = false;
        this.connectionFailures++;
        
        // 如果达到最大失败次数，禁用功能
        if (this.connectionFailures >= this.maxConnectionFailures) {
          this.isEnabled = false;
          return;
        }

        // 尝试重连
        if (this.isEnabled && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      });

      // 断开连接事件
      this.socket.on('disconnect', (reason) => {
        this.isConnecting = false;
        this.stopHeartbeat();
        
        // 如果是非主动断开且功能仍启用，尝试重连
        if (this.isEnabled && reason !== 'io client disconnect' && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      });

    } catch (error) {
      this.isConnecting = false;
      this.connectionFailures++;
    }
  }

  handleMessage(data) {
    // 处理权限更新消息
    if (data.type === 'permission-update') {
      if (data.payload && data.payload.permissions) {
        // 触发全局权限更新事件
        window.dispatchEvent(new CustomEvent('permissionsUpdated', {
          detail: { 
            permissions: data.payload.permissions,
            source: 'websocket'
          }
        }));
      }
    }

    // 处理主动登出成功消息
    if (data.type === 'logout-success') {
      // 这是用户主动登出的确认消息，不需要特殊处理
      // 因为前端已经在调用登出接口后处理了清理工作
      return;
    }

    // 处理用户角色变更消息
    if (data.type === 'role-changed') {
      const { oldRole, newRole, message } = data.payload || {};
      
      if (window.antd && window.antd.Modal) {
        window.antd.Modal.warning({
          title: '账号权限已变更',
          content: message || `您的账号权限已从 ${oldRole} 变更为 ${newRole}`,
          okText: '重新登录',
          centered: true,
          closable: false,
          maskClosable: false,
          onOk: () => {
            // 清除本地存储的认证信息
            clearAuthData();
            
            // 断开WebSocket连接
            this.disconnect();
            
            // 跳转到登录页面重新登录
            const accountType = localStorage.getItem('accountType');
            window.location.href = accountType === 'ram' ? '/ram-login' : '/login';
          }
        });
      } else {
        // 如果antd Modal不可用，使用原生alert
        alert(message || `您的账号权限已变更，请重新登录`);
        clearAuthData();
        this.disconnect();
        const accountType = localStorage.getItem('accountType');
        window.location.href = accountType === 'ram' ? '/ram-login' : '/login';
      }
      
      return; // 提前返回，不处理其他消息处理器
    }

    // 处理账号被禁用消息
    if (data.type === 'account-disabled') {
      const { reason, message } = data.payload || {};
      
      // 清除本地存储的认证信息
      clearAuthData();
      
      // 断开WebSocket连接
      this.disconnect();
      
      if (window.antd && window.antd.Modal) {
        window.antd.Modal.error({
          title: '账号已被禁用',
          content: message || reason || '您的账号已被管理员禁用',
          okText: '确定',
          centered: true,
          closable: false,
          maskClosable: false,
          onOk: () => {
            // 跳转到登录页面
            const accountType = localStorage.getItem('accountType');
            window.location.href = accountType === 'ram' ? '/ram-login' : '/login';
          }
        });
      } else {
        // 如果antd Modal不可用，使用原生alert
        alert(message || reason || '您的账号已被管理员禁用');
        const accountType = localStorage.getItem('accountType');
        window.location.href = accountType === 'ram' ? '/ram-login' : '/login';
      }
      
      return; // 提前返回，不处理其他消息处理器
    }

    // 处理密码重置消息 - 静默强制重新登录
    if (data.type === 'password-changed') {
      // 清除本地存储的认证信息
      clearAuthData();
      
      // 断开WebSocket连接
      this.disconnect();
      
      // 静默跳转到RAM登录页面
      window.location.href = '/ram-login';
      
      return; // 提前返回，不处理其他消息处理器
    }

    // 处理单点登录踢出消息
    if (data.type === 'force-logout' || data.type === 'session-kicked') {
      // 获取账号类型
      const accountType = localStorage.getItem('accountType') || 'main';
      
      // 清除本地存储的认证信息
      clearAuthData();
      
      // 断开WebSocket连接
      this.disconnect();
      
      // 获取踢出原因
      const reason = data.payload?.reason || '您的账号已在其他设备登录';
      
      // 显示被踢出提示
      if (window.antd && window.antd.Modal) {
        window.antd.Modal.warning({
          title: '登录状态失效',
          content: reason,
          okText: '确定',
          centered: true,
          onOk: () => {
            // 根据账号类型跳转
            window.location.href = accountType === 'ram' ? '/ram-login' : '/login';
          }
        });
      } else {
        // 如果antd Modal不可用，使用原生alert
        alert(reason);
                  window.location.href = accountType === 'ram' ? '/ram-login' : '/login';
      }
      
      return; // 提前返回，不处理其他消息处理器
    }

    // 调用注册的消息处理器
    const handler = this.messageHandlers.get(data.type);
    if (handler) {
      handler(data);
    }
  }

  send(data) {
    if (this.socket && this.socket.connected) {
      this.socket.emit('message', data);
      return true;
    } else {
      return false;
    }
  }

  scheduleReconnect() {
    // 检查功能是否仍然启用
    if (!this.isEnabled) {
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1);

    setTimeout(() => {
      if (this.isEnabled && this.reconnectAttempts <= this.maxReconnectAttempts) {
        const accountType = localStorage.getItem('accountType');
        this.connect(accountType);
      } else if (this.reconnectAttempts > this.maxReconnectAttempts) {
        this.isEnabled = false;
      }
    }, delay);
  }

  addMessageHandler(type, handler) {
    this.messageHandlers.set(type, handler);
  }

  removeMessageHandler(type) {
    this.messageHandlers.delete(type);
  }

  // 发送心跳（Socket.IO有内置心跳，这里主要用于应用层）
  sendHeartbeat() {
    return this.send({ type: 'ping', timestamp: Date.now() });
  }

  // 启动心跳
  startHeartbeat(interval = websocketConfig.connection.heartbeatInterval) {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
    }

    this.heartbeatTimer = setInterval(() => {
      if (!this.sendHeartbeat()) {
        // 心跳发送失败，可能需要重连
      }
    }, interval);
  }

  // 停止心跳
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  disconnect() {
    // 主动断开Socket.IO连接
    this.stopHeartbeat();
    
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
    }
    
    this.reconnectAttempts = this.maxReconnectAttempts; // 阻止自动重连
    this.isConnecting = false;
  }

  // 检查Socket.IO是否可用
  isAvailable() {
    return this.isEnabled && typeof io !== 'undefined';
  }

  // 获取Socket.IO状态信息
  getStatus() {
    return {
      enabled: this.isEnabled,
      connected: this.isConnected(),
      connectionFailures: this.connectionFailures,
      reconnectAttempts: this.reconnectAttempts,
      available: this.isAvailable(),
      socketId: this.socket?.id || null
    };
  }

  getConnectionState() {
    if (!this.socket) return 'CLOSED';
    
    if (this.socket.connected) return 'OPEN';
    if (this.socket.connecting) return 'CONNECTING';
    return 'CLOSED';
  }

  isConnected() {
    return this.socket && this.socket.connected;
  }
}

// 创建全局Socket.IO服务实例
const websocketService = new WebSocketService();

export default websocketService; 